import { type QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import type { ListDocumentApiResponses } from "@recobra/schemas/document";

import { documentsApi } from "../lib/api";

export const useDeleteDocumentMutation = (documentsQueryKey: QueryKey) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => documentsApi.deleteDocument(documentId),
    onMutate: async (documentId) => {
      await queryClient.cancelQueries({ queryKey: documentsQueryKey });
      const previousDocuments =
        queryClient.getQueryData<ListDocumentApiResponses>(documentsQueryKey);

      queryClient.setQueryData<ListDocumentApiResponses>(documentsQueryKey, (old) =>
        old
          ? {
              ...old,
              items: old.items.filter((item) => item.id !== documentId),
            }
          : old,
      );

      return { previousDocuments };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDocuments) {
        queryClient.setQueryData(documentsQueryKey, context.previousDocuments);
      }
      toast.error(m.doc_delete_error());
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: documentsQueryKey });
    },
  });
};
